// src/core/vec3.ts — Düz {x,y,z} vektör yardımcıları. three'siz; hepsi out parametreli ve tahsissiz (yalnız v3() yaratır).
// Rig ileri ekseni +Z, sol +X (ölçüm): yaw → ileri (sin, 0, cos), sol (cos, 0, −sin). THREE.Vector3 yapısal olarak uyumludur.
import type { Vec3 } from './types.ts';

/** Yeni vektör — YALNIZ kurulum/ yaratılış anında (sıcak yolda tahsis yasak). */
export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export function v3set(out: Vec3, x: number, y: number, z: number): Vec3 { out.x = x; out.y = y; out.z = z; return out; }
export function v3copy(out: Vec3, a: Vec3): Vec3 { out.x = a.x; out.y = a.y; out.z = a.z; return out; }
export function v3add(out: Vec3, a: Vec3, b: Vec3): Vec3 { out.x = a.x + b.x; out.y = a.y + b.y; out.z = a.z + b.z; return out; }
export function v3sub(out: Vec3, a: Vec3, b: Vec3): Vec3 { out.x = a.x - b.x; out.y = a.y - b.y; out.z = a.z - b.z; return out; }
export function v3scale(out: Vec3, a: Vec3, s: number): Vec3 { out.x = a.x * s; out.y = a.y * s; out.z = a.z * s; return out; }
/** out = a + b·s (hareket adımı: pos + vel·DT). */
export function v3scaleAdd(out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 { out.x = a.x + b.x * s; out.y = a.y + b.y * s; out.z = a.z + b.z * s; return out; }
export function v3lerp(out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 {
  out.x = a.x + (b.x - a.x) * t; out.y = a.y + (b.y - a.y) * t; out.z = a.z + (b.z - a.z) * t; return out;
}
export function v3dot(a: Vec3, b: Vec3): number { return a.x * b.x + a.y * b.y + a.z * b.z; }
export function v3len(a: Vec3): number { return Math.hypot(a.x, a.y, a.z); }
export function v3dist(a: Vec3, b: Vec3): number { return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z); }

/** Yatay (XZ) uzunluk / mesafe — kapsül testleri ve nişan yardımı hep XZ'de çalışır. */
export function lenXZ(a: Vec3): number { return Math.hypot(a.x, a.z); }
export function distXZ(a: Vec3, b: Vec3): number { return Math.hypot(a.x - b.x, a.z - b.z); }

// ── Yaw ↔ yön (rig ileri +Z, sol +X) ──
/** Bakış yönünün dünya X bileşeni. */
export const fwdX = (yaw: number): number => Math.sin(yaw);
/** Bakış yönünün dünya Z bileşeni. */
export const fwdZ = (yaw: number): number => Math.cos(yaw);
/** Sol yönün dünya X / Z bileşeni (sol = +X model uzayı → dünyada (cos, −sin)). */
export const leftX = (yaw: number): number => Math.cos(yaw);
export const leftZ = (yaw: number): number => -Math.sin(yaw);
/** Yatay yön vektöründen yaw (viewer/player.js: rotation.y = atan2(vx, vz)). (0,0) → 0. */
export const yawOf = (x: number, z: number): number => (x === 0 && z === 0 ? 0 : Math.atan2(x, z));
/** Açıyı (−π, π] aralığına sarar. */
export function wrapAngle(a: number): number {
  const TAU = Math.PI * 2;
  a = a % TAU;
  if (a > Math.PI) a -= TAU;
  else if (a <= -Math.PI) a += TAU;
  return a;
}
/** İki yaw arasındaki en kısa işaretli fark (b − a), (−π, π]. */
export const yawDelta = (a: number, b: number): number => wrapAngle(b - a);

/** Model-yerel (fwd, up, side) ofsetini dünya konumuna çevirir: out = origin + fwd·F(yaw) + up·Y + side·L(yaw).
 *  side > 0 = karakterin SOLU (rig +X). Hitbox konumu (ARCHITECTURE §4.5) ve soketler bunu kullanır. */
export function localToWorld(out: Vec3, origin: Vec3, yaw: number, fwd: number, up: number, side: number): Vec3 {
  const s = Math.sin(yaw), c = Math.cos(yaw);
  out.x = origin.x + fwd * s + side * c;
  out.y = origin.y + up;
  out.z = origin.z + fwd * c - side * s;
  return out;
}
/** Dünya yatay vektörünü karakter-yerel (fwd, side) bileşenlerine ayırır; out.x = fwd, out.z = side, out.y = 0. */
export function worldToLocalXZ(out: Vec3, yaw: number, dx: number, dz: number): Vec3 {
  const s = Math.sin(yaw), c = Math.cos(yaw);
  out.x = dx * s + dz * c;      // ileri bileşen
  out.y = 0;
  out.z = dx * c - dz * s;      // sol bileşen
  return out;
}
